<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>静态路由管理</strong>
        <small class="text-muted ml-2">/ip/route + /ipv6/route（RouterOS 7，含 blackhole 汇总路由）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openModal()">新增路由</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-for="r in routers" :key="r.router_id" class="mb-3">
          <div class="route-router-header">
            <strong>{{ r.router_name }}</strong>
            <small class="text-muted ml-2">{{ r.router_host }}</small>
            <CBadge color="info" class="ml-2">{{ r.routes.length }} 条</CBadge>
            <span class="ml-auto">
              <CButton color="primary" size="sm" @click="openModal(null, r)">新增路由</CButton>
            </span>
          </div>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th width="180">Dst Address</th>
                <th>Gateway</th>
                <th width="90">类型</th>
                <th width="100">Routing Table</th>
                <th width="70">Distance</th>
                <th width="100">Check GW</th>
                <th width="70">Scope</th>
                <th width="100">状态</th>
                <th width="200">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in r.routes" :key="t.id" :class="{ 'text-muted': !t.enabled }">
                <td>
                  <CBadge :color="t.family === 'ipv6' ? 'dark' : 'light'" class="mr-1">{{ t.family }}</CBadge>
                  <code>{{ t.dst_address }}</code>
                </td>
                <td><code>{{ t.gateway || '-' }}</code></td>
                <td>
                  <CBadge :color="t.route_type === 'blackhole' ? 'danger' : (t.route_type !== 'normal' ? 'warning' : 'success')">
                    {{ t.route_type }}
                  </CBadge>
                </td>
                <td>{{ t.routing_table }}</td>
                <td>{{ t.distance }}</td>
                <td>{{ t.check_gateway || '-' }}</td>
                <td><small>{{ t.scope }} / {{ t.target_scope }}</small></td>
                <td>
                  <CBadge v-if="t.source === 'group'" color="info">组 {{ t.group_name }}</CBadge>
                  <CBadge v-else-if="t.enabled" color="success">启用</CBadge>
                  <CBadge v-else color="secondary">禁用</CBadge>
                </td>
                <td>
                  <CButtonGroup v-if="t.source === 'group'" size="sm">
                    <CButton color="light" size="sm" disabled>由路由组管理</CButton>
                  </CButtonGroup>
                  <CButtonGroup v-else size="sm">
                    <CButton color="info" size="sm" @click="openModal(t, r)">编辑</CButton>
                    <CButton color="warning" size="sm" @click="toggleItem(t)">{{ t.enabled ? '禁用' : '启用' }}</CButton>
                    <CButton color="danger" size="sm" @click="deleteItem(t)">删除</CButton>
                  </CButtonGroup>
                </td>
              </tr>
              <tr v-if="r.routes.length === 0">
                <td colspan="9" class="text-center text-muted">暂无静态路由</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- 编辑 Modal -->
    <CModal :show.sync="modal.show" :title="modal.isEdit ? '编辑静态路由' : '新增静态路由'" color="primary" size="lg">
      <CForm>
        <CSelect
          v-if="!modal.isEdit"
          label="路由器"
          v-model="modal.form.router_id"
          :options="routerOptions"
          placeholder="请选择路由器"
        />
        <CRow>
          <CCol sm="5">
            <CSelect label="地址族" v-model="modal.form.family" :disabled="modal.isEdit" :options="[
              { value: 'ipv4', label: 'ipv4 (/ip/route)' },
              { value: 'ipv6', label: 'ipv6 (/ipv6/route)' }
            ]" />
          </CCol>
          <CCol sm="4">
            <CSelect label="类型" v-model="modal.form.route_type" :options="[
              { value: 'normal', label: 'normal（网关路由）' },
              { value: 'blackhole', label: 'blackhole（黑洞汇总）' },
              { value: 'unreachable', label: 'unreachable' },
              { value: 'prohibit', label: 'prohibit' }
            ]" />
          </CCol>
          <CCol sm="3">
            <CInput label="Distance" v-model="modal.form.distance" placeholder="如 1" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="5">
            <CInput label="Dst Address（目标网段）" v-model="modal.form.dst_address" placeholder="如 172.16.0.0/16（汇总）或 0.0.0.0/0" />
          </CCol>
          <CCol sm="4">
            <CInput
              label="Gateway（普通路由必填）"
              v-model="modal.form.gateway"
              :disabled="modal.form.route_type !== 'normal'"
              :placeholder="modal.form.route_type === 'normal' ? '如 25.0.0.101 / 接口名 / 域名' : '（黑洞路由无需网关）'"
            />
          </CCol>
          <CCol sm="3">
            <CInput label="Routing Table" v-model="modal.form.routing_table" placeholder="main" />
          </CCol>
        </CRow>
        <CRow>
          <CCol sm="3">
            <CSelect label="Check Gateway" v-model="modal.form.check_gateway" :options="[
              { value: '', label: '（不设置）' },
              { value: 'ping', label: 'ping' },
              { value: 'arp', label: 'arp' },
              { value: 'none', label: 'none' },
              { value: 'bfd', label: 'bfd' }
            ]" />
          </CCol>
          <CCol sm="3">
            <CInput label="Scope" v-model="modal.form.scope" placeholder="如 30" />
          </CCol>
          <CCol sm="3">
            <CInput label="Target Scope" v-model="modal.form.target_scope" placeholder="如 10" />
          </CCol>
          <CCol sm="3">
            <CInput label="Pref Src" v-model="modal.form.pref_src" placeholder="（可选）" />
          </CCol>
        </CRow>
        <CAlert color="info" fade>
          汇总路由（如 172.16.0.0/16 抑制明细）使用 blackhole 类型；BGP 通告网段请配合 Address Lists + output.network。
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="modal.show = false">取消</CButton>
          <CButton color="success" :disabled="modal.loading" @click="submitItem">
            {{ modal.loading ? '保存中...' : '保存' }}
          </CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'StaticRouteList',
  data() {
    return {
      loading: false,
      routers: [],
      allRouters: [],
      modal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        form: this.emptyForm()
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    routerOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: `${r.name} (${r.host})` }))
    }
  },
  mounted() {
    this.loadList()
    this.loadAllRouters()
  },
  methods: {
    emptyForm() {
      return {
        router_id: '', family: 'ipv4', route_type: 'normal', dst_address: '',
        gateway: '', routing_table: 'main', distance: '', check_gateway: '',
        scope: '', target_scope: '', pref_src: ''
      }
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/route/list')
        if (res.data.code === 200) {
          this.routers = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    openModal(route, routerRef) {
      if (route) {
        this.modal.isEdit = true
        this.modal.editId = route.id
        this.modal.form = {
          router_id: '',
          family: route.family,
          route_type: route.route_type || 'normal',
          dst_address: route.dst_address,
          gateway: route.gateway || '',
          routing_table: route.routing_table || 'main',
          distance: route.distance !== null && route.distance !== undefined ? String(route.distance) : '',
          check_gateway: route.check_gateway || '',
          scope: route.scope !== null && route.scope !== undefined ? String(route.scope) : '',
          target_scope: route.target_scope !== null && route.target_scope !== undefined ? String(route.target_scope) : '',
          pref_src: route.pref_src || ''
        }
      } else {
        this.modal.isEdit = false
        this.modal.editId = null
        this.modal.form = this.emptyForm()
        if (routerRef) this.modal.form.router_id = routerRef.router_id
      }
      this.modal.show = true
    },
    async submitItem() {
      const f = this.modal.form
      if (!this.modal.isEdit && !f.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      if (!f.dst_address) {
        this.$toasted.show('请填写 Dst Address', { type: 'error' })
        return
      }
      if (f.route_type === 'normal' && !f.gateway) {
        this.$toasted.show('普通路由必须填写 Gateway', { type: 'error' })
        return
      }
      this.modal.loading = true
      try {
        let res
        if (this.modal.isEdit) {
          res = await this.$axios.post(`user/route/${this.modal.editId}/edit`, f)
        } else {
          res = await this.$axios.post('user/route/add', f)
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.modal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.modal.loading = false
    },
    toggleItem(item) {
      this.showConfirm('确认操作', `确认${item.enabled ? '禁用' : '启用'}路由 ${item.dst_address}？`, async () => {
        try {
          const res = await this.$axios.post(`user/route/${item.id}/toggle`)
          if (res.data.code === 200) {
            this.$toasted.show('已切换', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '操作失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    deleteItem(item) {
      this.showConfirm('确认删除', `确认删除路由 ${item.dst_address}（${item.routing_table}）？`, async () => {
        try {
          const res = await this.$axios.delete(`user/route/${item.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.route-router-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
  margin-bottom: 8px;
}
.ml-auto {
  margin-left: auto;
}
</style>
